package com.evoapps.evoshift.widgets

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.TextAutoSize
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.automirrored.outlined.HelpOutline
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material3.Badge
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isAltPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalUriHandler
import com.evoapps.evoshift.ui.theme.sharedTopBar
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.adaptive.wheelSteps
import com.evoapps.evoshift.navigation.AppShortcut
import com.evoapps.evoshift.navigation.ShortcutHandler
import com.evoapps.evoshift.ui.util.HELP_CENTRE_TAG
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.ui.util.monthName
import com.evoapps.evoshift.util.appLanguage
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.back
import evoshift.composeapp.generated.resources.dashboard_workplace_switch
import evoshift.composeapp.generated.resources.help_centre
import evoshift.composeapp.generated.resources.roster_next_month
import evoshift.composeapp.generated.resources.roster_previous_month
import evoshift.composeapp.generated.resources.top_bar_month_picker_title
import evoshift.composeapp.generated.resources.top_bar_more
import evoshift.composeapp.generated.resources.top_bar_next_year
import evoshift.composeapp.generated.resources.top_bar_pick_month
import evoshift.composeapp.generated.resources.top_bar_previous_year
import evoshift.composeapp.generated.resources.top_bar_this_month
import kotlinx.datetime.YearMonth
import kotlinx.datetime.minusMonth
import kotlinx.datetime.number
import kotlinx.datetime.plusMonth
import org.jetbrains.compose.resources.stringResource

/*
 * SH-89: one top bar for the app: the workplace (switchable when the person has more than one),
 * the month "‹ Październik 2026 ›" (tap: a month/year picker) and the screen's actions, navy and
 * gold. docs/design/top-bar.md has which screen shows what and the small-screen rules.
 */

/** Test tags of the bar's parts, for robots. */
object TopBarTags {
    const val BAR = "top_bar"
    const val WORKPLACE = "top_bar_workplace"
    const val MONTH = "top_bar_month"
    const val PREVIOUS = "top_bar_previous_month"
    const val NEXT = "top_bar_next_month"
    const val OVERFLOW = "top_bar_overflow"
    const val PICKER_YEAR = "top_bar_picker_year"
    fun pickerMonth(month: Int) = "top_bar_picker_month_$month"
}

/** An action at the bar's end. */
@Immutable
data class TopBarAction(
    val label: String,
    val icon: ImageVector?,
    val onClick: () -> Unit,
    val enabled: Boolean = true,
    /** A gold text button (Publish); never goes into the ⋮ menu. */
    val prominent: Boolean = false,
    /** A prominent action shown as its [icon] on a phone (its label is the description). */
    val iconOnCompact: Boolean = false,
    /** SH-215: a white text button with its [icon] on a wide window; an icon (with a tooltip) on a phone. */
    val labelled: Boolean = false,
    /** May go into the ⋮ menu on a phone when the bar runs out of room (help, print). */
    val collapsible: Boolean = true,
    val badge: Int = 0,
    val testTag: String? = null,
    /** Working (a save in flight): a small spinner instead of the icon or before the label. */
    val busy: Boolean = false,
    /** SH-229: always in ⋮, on every width (the less used ones: help, print, import). */
    val inMenu: Boolean = false,
    /**
     * SH-229: the screen's main action at the bar's very end (after ⋮, never in it): a filled gold
     * button, or [PrimaryStyle.DONE] when there is nothing left to do (e.g. "Published").
     */
    val primary: PrimaryStyle? = null,
    /** SH-229: a shorter [label] for a [primary] button on a phone ("Publish" for "Publish changes"). */
    val compactLabel: String? = null
) {
    /** Shown as a gold text button (else an icon). */
    internal fun isText(compact: Boolean): Boolean = when (primary) {
        PrimaryStyle.FILLED -> true
        PrimaryStyle.DONE -> !compact || icon == null
        null -> (prominent && !(compact && iconOnCompact && icon != null)) || (labelled && !compact && icon != null)
    }

    /** 48 dp slots it takes in the bar. */
    internal fun slots(compact: Boolean): Int = if (isText(compact)) 2 else 1
}

/** SH-229: how a [TopBarAction.primary] action looks. */
enum class PrimaryStyle {
    /** A filled gold button: the thing to do next (Publish). */
    FILLED,
    /** A quiet "done" state with a tick (Published); still pressable. */
    DONE
}

/**
 * What the bar shows and changes: the person's workplaces and the app-wide month
 * (session/WorkplaceMonthSelection), provided once by App.kt.
 */
@Immutable
class TopBarSelection(
    val workplaces: Map<String, String>,
    val workplaceId: String?,
    val month: YearMonth,
    val currentMonth: YearMonth,
    /** Months marked in the picker (those with a roster). */
    val markedMonths: Set<YearMonth> = emptySet(),
    /** Opens the workplace chooser (the context sheet's workplaces). */
    val onSwitchWorkplace: () -> Unit,
    val onMonth: (YearMonth) -> Unit
) {
    val workplaceName: String? get() = workplaceId?.let { workplaces[it] }

    /** The same selection with other months marked in the picker (e.g. those with a request). */
    fun withMarkedMonths(months: Set<YearMonth>) =
        TopBarSelection(workplaces, workplaceId, month, currentMonth, months, onSwitchWorkplace, onMonth)
    val canSwitchWorkplace: Boolean get() = workplaces.size > 1
}

val LocalTopBarSelection = compositionLocalOf<TopBarSelection?> { null }

/**
 * Counts the navy bars on screen, so App.kt gives the status bar light icons wherever one shows
 * (the dashboard route also shows the cream dashboards of other roles).
 */
@Stable
class NavyTopBars {
    var count by mutableIntStateOf(0)
        internal set
}

val LocalNavyTopBars = staticCompositionLocalOf<NavyTopBars?> { null }

/** The help centre's "?" for [page] (SH-77), as a bar action. */
@Composable
fun helpAction(page: HelpPage): TopBarAction {
    val uriHandler = LocalUriHandler.current
    val language = appLanguage()
    return TopBarAction(
        label = stringResource(Res.string.help_centre),
        icon = Icons.AutoMirrored.Outlined.HelpOutline,
        onClick = {
            AppTelemetry.log(AnalyticsEvent.HelpOpened(page.slug))
            uriHandler.openUri(page.url(language))
        },
        testTag = HELP_CENTRE_TAG
    )
}

/**
 * The app's top bar (SH-89).
 *
 * * [title]: secondary (small, gold) above the workplace; left out where the bottom bar or the
 *   month already says where the person is.
 * * [showWorkplace]: the workplace's name; with more than one workplace it opens the chooser.
 * * [showMonth]: "‹ Październik 2026 ›"; the arrows step a month, the name opens the picker.
 *   Never shortened: on a phone in portrait it gets a row of its own.
 * * [actions]: at the end; on a phone the collapsible ones go into ⋮ when they do not fit.
 *
 * Keyboard (desktop, web): every part is focusable; Alt+← / Alt+→ and Page Up / Page Down step
 * the month while the focus is in the bar, Ctrl+[ / Ctrl+] anywhere on the screen (SH-98). A
 * mouse wheel or a two-finger swipe over the month steps it too.
 */
@Composable
fun EvoTopBar(
    modifier: Modifier = Modifier,
    title: String? = null,
    onBack: (() -> Unit)? = null,
    showWorkplace: Boolean = false,
    showMonth: Boolean = false,
    actions: List<TopBarAction> = emptyList(),
    /** The back arrow's description (e.g. "Cancel" on a form); "Back" by default. */
    backDescription: String? = null,
    selection: TopBarSelection? = LocalTopBarSelection.current
) {
    val navyBars = LocalNavyTopBars.current
    if (navyBars != null) {
        DisposableEffect(navyBars) {
            navyBars.count++
            onDispose { navyBars.count-- }
        }
    }
    val compact = LocalWindowWidthClass.current.isCompact
    val month = selection?.month?.takeIf { showMonth }
    val workplace = selection?.takeIf { showWorkplace && it.workplaceName != null }
    var picking by remember { mutableStateOf(false) }

    val stepMonth: (Int) -> Unit = { delta ->
        if (selection != null && month != null) selection.onMonth(if (delta < 0) month.minusMonth() else month.plusMonth())
    }
    // SH-98: Ctrl+[ / Ctrl+] (and Ctrl+← / Ctrl+→) step the month wherever the bar shows it.
    val monthShown = selection != null && month != null
    ShortcutHandler(AppShortcut.PREVIOUS_MONTH, enabled = monthShown) { stepMonth(-1) }
    ShortcutHandler(AppShortcut.NEXT_MONTH, enabled = monthShown) { stepMonth(1) }

    Column(
        // SH-103: stays put while the screens change under it; only its contents cross-fade.
        modifier = Modifier.sharedTopBar()
            .then(modifier)
            .fillMaxWidth()
            .background(DarkMetallicBackgroundBrush)
            .windowInsetsPadding(TopAppBarDefaults.windowInsets)
            .testTag(TopBarTags.BAR)
            .onPreviewKeyEvent { event ->
                if (month == null || event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                when {
                    event.key == Key.PageUp || (event.isAltPressed && event.key == Key.DirectionLeft) -> { stepMonth(-1); true }
                    event.key == Key.PageDown || (event.isAltPressed && event.key == Key.DirectionRight) -> { stepMonth(1); true }
                    else -> false
                }
            }
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().heightIn(min = 56.dp).padding(horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (onBack != null) {
                IconButtonWithTooltip(onClick = onBack, modifier = Modifier.handCursor(), tooltip = backDescription ?: stringResource(Res.string.back)) {
                    Icon(Icons.AutoMirrored.Filled.ArrowBack, backDescription ?: stringResource(Res.string.back), tint = Color.White)
                }
            } else {
                Spacer(Modifier.width(12.dp))
            }
            // SH-229: on a wide window the month sits right after the workplace (where the person is
            // and when, together on the left); the actions keep the right. The workplace and the
            // title give way to everything else (they may shorten; the month never).
            Row(Modifier.weight(1f), verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.weight(1f, fill = false), verticalArrangement = Arrangement.Center) {
                if (title != null && workplace == null && month == null) {
                    // A screen of its own (settings, profile, …): the title is the bar's text.
                    Text(
                        title,
                        color = Color.White,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        autoSize = TextAutoSize.StepBased(minFontSize = 15.sp, maxFontSize = 22.sp, stepSize = 1.sp),
                        modifier = Modifier.padding(start = 4.dp)
                    )
                } else if (title != null) {
                    Text(
                        title,
                        color = LightGold.copy(alpha = 0.9f),
                        fontSize = 12.sp,
                        letterSpacing = 0.4.sp,
                        fontWeight = FontWeight.SemiBold,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.padding(start = 4.dp)
                    )
                }
                if (workplace != null) WorkplaceChip(workplace)
            }
            if (month != null && !compact) {
                Spacer(Modifier.width(8.dp))
                MonthSwitcher(month, onStep = stepMonth, onPick = { picking = true })
            }
            }
            Spacer(Modifier.width(8.dp))
            BarActions(actions, compact)
        }
        if (month != null && compact) {
            Box(Modifier.fillMaxWidth().padding(bottom = 4.dp), contentAlignment = Alignment.Center) {
                MonthSwitcher(month, onStep = stepMonth, onPick = { picking = true })
            }
        }
        // SH-148: "Saving…" while any save of the app runs, on every screen's bar.
        com.evoapps.evoshift.saves.SavingIndicator(color = LightGold)
        // The gold hairline that closes the bar.
        Box(Modifier.fillMaxWidth().height(1.dp).background(GoldMetallicBrush))
    }

    if (picking && selection != null && month != null) {
        MonthPickerDialog(
            selected = month,
            current = selection.currentMonth,
            marked = selection.markedMonths,
            onDismiss = { picking = false },
            onPick = {
                picking = false
                selection.onMonth(it)
            }
        )
    }
}

/** The workplace's initial in a gold ring and its name; ▾ and a tap when there is another one. */
@Composable
private fun WorkplaceChip(selection: TopBarSelection) {
    val name = selection.workplaceName.orEmpty()
    val canSwitch = selection.canSwitchWorkplace
    val description = stringResource(Res.string.dashboard_workplace_switch)
    Row(
        modifier = Modifier
            .heightIn(min = 48.dp)
            .clip(RoundedCornerShape(12.dp))
            .then(
                if (canSwitch) Modifier
                    .handCursor()
                    .clickable(role = Role.Button, onClick = selection.onSwitchWorkplace)
                    .semantics { contentDescription = description }
                // Read as one: the initial and the name.
                else Modifier.semantics(mergeDescendants = true) {}
            )
            .testTag(TopBarTags.WORKPLACE)
            .padding(horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier.size(28.dp).clip(CircleShape).border(1.5.dp, GoldMetallicBrush, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Text(name.trim().firstOrNull()?.uppercase() ?: "•", color = LightGold, fontWeight = FontWeight.Bold, fontSize = 13.sp)
        }
        Spacer(Modifier.width(8.dp))
        Text(
            name,
            color = Color.White,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            // One line on every size (a second line pushed the bar's actions out of line on 402 dp
            // phones): a long name gets smaller, then it is shortened with an ellipsis.
            maxLines = 1,
            lineHeight = 17.sp,
            overflow = TextOverflow.Ellipsis,
            autoSize = TextAutoSize.StepBased(minFontSize = 12.sp, maxFontSize = 16.sp, stepSize = 1.sp),
            modifier = Modifier.weight(1f, fill = false)
        )
        if (canSwitch) Icon(Icons.Default.ArrowDropDown, contentDescription = null, tint = DarkGold)
    }
}

/** "‹ Październik 2026 ›": 48 dp arrows, the month's whole name opens the picker. */
@Composable
private fun MonthSwitcher(month: YearMonth, onStep: (Int) -> Unit, onPick: () -> Unit) {
    val label = "${monthName(month.month.number)} ${month.year}"
    val pickDescription = stringResource(Res.string.top_bar_pick_month, label)
    // SH-98: the wheel over the month steps it (down: the next month), as in a desktop calendar.
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.wheelSteps(onStep)) {
        IconButtonWithTooltip(onClick = { onStep(-1) }, modifier = Modifier.handCursor().testTag(TopBarTags.PREVIOUS), tooltip = stringResource(Res.string.roster_previous_month)) {
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, stringResource(Res.string.roster_previous_month), tint = LightGold)
        }
        Box(
            modifier = Modifier
                .heightIn(min = 48.dp)
                .clip(RoundedCornerShape(12.dp))
                .handCursor()
                .clickable(role = Role.Button, onClick = onPick)
                .semantics { contentDescription = pickDescription }
                .testTag(TopBarTags.MONTH)
                .padding(horizontal = 10.dp),
            contentAlignment = Alignment.Center
        ) {
            // Never ellipsized: the label keeps its whole width (no wrapping), the rest gives way.
            Text(label, color = Color.White, fontSize = 17.sp, fontWeight = FontWeight.Bold, maxLines = 1, softWrap = false)
        }
        IconButtonWithTooltip(onClick = { onStep(1) }, modifier = Modifier.handCursor().testTag(TopBarTags.NEXT), tooltip = stringResource(Res.string.roster_next_month)) {
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, stringResource(Res.string.roster_next_month), tint = LightGold)
        }
    }
}

/**
 * The actions; on a phone they get [COMPACT_SLOTS] 48 dp slots (a prominent one takes two). When
 * they do not fit, the collapsible ones go into ⋮. SH-229: [TopBarAction.inMenu] ones are always
 * in ⋮, and the [TopBarAction.primary] one comes last, after ⋮.
 */
@Composable
private fun BarActions(actions: List<TopBarAction>, compact: Boolean) {
    val (bar, overflow) = arrangeActions(actions, compact)
    val (primary, inline) = bar.partition { it.primary != null }
    inline.forEach { BarAction(it, compact) }
    if (overflow.isNotEmpty()) {
        var open by remember { mutableStateOf(false) }
        Box {
            IconButtonWithTooltip(onClick = { open = true }, modifier = Modifier.handCursor().testTag(TopBarTags.OVERFLOW), tooltip = stringResource(Res.string.top_bar_more)) {
                Icon(Icons.Default.MoreVert, stringResource(Res.string.top_bar_more), tint = Color.White)
            }
            DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
                overflow.forEach { action ->
                    DropdownMenuItem(
                        text = { Text(action.label) },
                        leadingIcon = action.icon?.let { icon -> { Icon(icon, contentDescription = null) } },
                        enabled = action.enabled,
                        onClick = { open = false; action.onClick() },
                        modifier = Modifier.handCursor().then(action.testTag?.let { Modifier.testTag(it) } ?: Modifier)
                    )
                }
            }
        }
    } else if (primary.isEmpty()) {
        Spacer(Modifier.width(4.dp))
    }
    primary.forEach { PrimaryBarAction(it, compact) }
}

/**
 * SH-229: the screen's main action at the bar's end: a filled gold button (its icon only on a
 * wide window, the [TopBarAction.compactLabel] on a phone), or the quiet "done" state: a tick and
 * the label in soft white (on a phone the tick alone, with the label as its tooltip).
 */
@Composable
private fun PrimaryBarAction(action: TopBarAction, compact: Boolean) {
    val tagged = Modifier.handCursor().then(action.testTag?.let { Modifier.testTag(it) } ?: Modifier)
    val label = if (compact) action.compactLabel ?: action.label else action.label
    when {
        // A pill sized for the bar (40 dp, snug padding), so the workplace keeps its room on a phone.
        action.primary == PrimaryStyle.FILLED -> {
            val active = action.enabled || action.busy
            BrushButton(
                onClick = action.onClick,
                brush = if (active) GoldMetallicBrush else androidx.compose.ui.graphics.SolidColor(Color.White.copy(alpha = 0.12f)),
                shape = RoundedCornerShape(12.dp),
                contentColor = DeepNavy,
                disabledContentColor = if (action.busy) DeepNavy else Color.White.copy(alpha = 0.45f),
                enabled = action.enabled && !action.busy,
                contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = if (compact) 12.dp else 16.dp),
                modifier = tagged.padding(start = 4.dp, end = 8.dp).heightIn(min = 40.dp)
            ) {
                if (action.busy) {
                    CircularProgressIndicator(color = DeepNavy, strokeWidth = 2.dp, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(8.dp))
                } else if (!compact) action.icon?.let { icon ->
                    Icon(icon, contentDescription = null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                }
                Text(label, fontWeight = FontWeight.Bold, maxLines = 1)
            }
        }
        action.isText(compact) -> TextButton(onClick = action.onClick, enabled = action.enabled, modifier = tagged.heightIn(min = 48.dp).padding(end = 4.dp)) {
            action.icon?.let { icon ->
                Icon(icon, contentDescription = null, tint = LightGold.copy(alpha = 0.85f), modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(6.dp))
            }
            Text(label, color = Color.White.copy(alpha = 0.75f), fontWeight = FontWeight.SemiBold)
        }
        else -> BarAction(action, compact)
    }
}

/** 48 dp slots for the actions on a phone. */
internal const val COMPACT_SLOTS = 3

/** Which [actions] show in the bar and which go into ⋮, with [slots] 48 dp slots for them. */
internal fun splitActions(actions: List<TopBarAction>, slots: Int, compact: Boolean = true): Pair<List<TopBarAction>, List<TopBarAction>> {
    if (actions.sumOf { it.slots(compact) } <= slots) return actions to emptyList()
    return actions.partition { !it.collapsible || it.prominent || it.primary != null }
}

/**
 * SH-229: the bar's actions and ⋮'s, in the order given: [TopBarAction.inMenu] ones always in ⋮;
 * on a phone also those that do not fit in [COMPACT_SLOTS] ([splitActions]).
 */
internal fun arrangeActions(actions: List<TopBarAction>, compact: Boolean): Pair<List<TopBarAction>, List<TopBarAction>> {
    val candidates = actions.filterNot { it.inMenu }
    val (bar, folded) = if (compact) splitActions(candidates, COMPACT_SLOTS, compact = true) else candidates to emptyList()
    return bar to actions.filter { it.inMenu || it in folded }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun BarAction(action: TopBarAction, compact: Boolean) {
    val tagged = Modifier.handCursor().then(action.testTag?.let { Modifier.testTag(it) } ?: Modifier)
    if (action.isText(compact)) {
        TextButton(onClick = action.onClick, enabled = action.enabled, modifier = tagged.heightIn(min = 48.dp)) {
            if (action.busy) {
                CircularProgressIndicator(color = DarkGold, strokeWidth = 2.dp, modifier = Modifier.size(16.dp))
                Spacer(Modifier.width(8.dp))
            }
            val color = if (action.prominent) DarkGold else Color.White
            if (!action.prominent && !action.busy) action.icon?.let { icon ->
                Icon(icon, contentDescription = null, tint = if (action.enabled) color else color.copy(alpha = 0.4f), modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(6.dp))
            }
            Text(action.label, color = if (action.enabled) color else color.copy(alpha = 0.4f), fontWeight = FontWeight.Bold)
        }
        return
    }
    // Icons say what they do on hover and long press too (desktop, web, phones).
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Below),
        tooltip = { PlainTooltip { Text(action.label) } },
        state = rememberTooltipState()
    ) {
        IconButton(onClick = action.onClick, enabled = action.enabled, modifier = tagged) {
            if (action.busy) {
                CircularProgressIndicator(color = DarkGold, strokeWidth = 2.dp, modifier = Modifier.size(22.dp))
                return@IconButton
            }
            val icon = action.icon ?: return@IconButton
            val tint = when {
                !action.enabled -> Color.White.copy(alpha = 0.3f)
                action.prominent -> DarkGold
                action.primary == PrimaryStyle.DONE -> LightGold
                else -> Color.White
            }
            when {
                action.badge > 0 -> BadgedBox(badge = {
                    Badge(containerColor = DarkGold, contentColor = DeepNavy) {
                        Text(if (action.badge > 9) "9+" else action.badge.toString())
                    }
                }) { Icon(icon, action.label, tint = tint) }
                else -> Icon(icon, action.label, tint = tint)
            }
        }
    }
}

/** The month/year picker: the year with arrows, the twelve months (a dot where there is a roster), "This month". */
@Composable
private fun MonthPickerDialog(
    selected: YearMonth,
    current: YearMonth,
    marked: Set<YearMonth>,
    onDismiss: () -> Unit,
    onPick: (YearMonth) -> Unit
) {
    var year by remember { mutableIntStateOf(selected.year) }
    EvoDialog(
        title = stringResource(Res.string.top_bar_month_picker_title),
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        confirm = DialogAction(stringResource(Res.string.top_bar_this_month), { onPick(current) }),
        bodyArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            IconButtonWithTooltip(onClick = { year-- }, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.top_bar_previous_year)) {
                Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, stringResource(Res.string.top_bar_previous_year))
            }
            Text(
                "$year",
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center,
                modifier = Modifier.weight(1f).testTag(TopBarTags.PICKER_YEAR)
            )
            IconButtonWithTooltip(onClick = { year++ }, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.top_bar_next_year)) {
                Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, stringResource(Res.string.top_bar_next_year))
            }
        }
        (1..12).chunked(3).forEach { row ->
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                row.forEach { m ->
                    val ym = YearMonth(year, m)
                    MonthCell(
                        name = monthName(m),
                        isSelected = ym == selected,
                        isCurrent = ym == current,
                        isMarked = ym in marked,
                        onClick = { onPick(ym) },
                        modifier = Modifier.weight(1f).testTag(TopBarTags.pickerMonth(m))
                    )
                }
            }
        }
    }
}

@Composable
private fun MonthCell(name: String, isSelected: Boolean, isCurrent: Boolean, isMarked: Boolean, onClick: () -> Unit, modifier: Modifier) {
    val shape = RoundedCornerShape(12.dp)
    Column(
        modifier = modifier
            .heightIn(min = 52.dp)
            .clip(shape)
            .then(if (isSelected) Modifier.background(GoldMetallicBrush) else Modifier)
            .then(if (isCurrent && !isSelected) Modifier.border(1.dp, DarkGold, shape) else Modifier)
            .handCursor()
            .clickable(role = Role.Button, onClick = onClick)
            .padding(horizontal = 2.dp, vertical = 6.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        // "Październik" in a third of a phone's dialog: smaller rather than cut.
        Text(
            name,
            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
            color = if (isSelected) DeepNavy else Color.Unspecified,
            maxLines = 1,
            softWrap = false,
            textAlign = TextAlign.Center,
            autoSize = TextAutoSize.StepBased(minFontSize = 10.sp, maxFontSize = 14.sp, stepSize = 1.sp)
        )
        // A roster exists for the month.
        Box(
            Modifier.padding(top = 3.dp).size(5.dp).clip(CircleShape)
                .background(if (isMarked) (if (isSelected) DeepNavy else DarkGold) else Color.Transparent)
        )
    }
}
